// Used by Studio before publishing. Reject mistakes before publishing;
// storefront normalization remains a separate last line of defence.
const keys = ['site', 'homepage', 'products', 'offers', 'gallery', 'testimonials', 'faq'];
const record = (value) => value && typeof value === 'object' && !Array.isArray(value);
const text = (value) => typeof value === 'string' && value.trim().length > 0;
const integer = (value, min, max) => Number.isInteger(value) && value >= min && value <= max;
const money = (value) => Number.isFinite(value) && value > 0 && value <= 1000000 && Math.abs(Math.round(value * 100) - value * 100) < 0.000001;

export function validateEntries(entries, current = {}) {
  const errors = [];
  const expect = (condition, message) => { if (!condition) errors.push(message); };
  if (!record(entries) || !Object.keys(entries).length) return ['Choose content to publish.'];
  const next = { ...current, ...entries };
  for (const [key, value] of Object.entries(entries)) {
    if (!keys.includes(key) || !record(value)) { errors.push(`Invalid ${key} content.`); continue; }
    const walk = (item, path) => {
      if (typeof item === 'string') {
        expect(item.length <= 5000, `${path}: keep text under 5,000 characters.`);
        if (/(?:url|image|poster|logo|video(?:mp4|webm|mobilemp4|mobilewebm)?)$/i.test(path) && item) {
          expect(/^\/(?!\/)/.test(item) || /^https:\/\//i.test(item) || /^http:\/\/(localhost|127\.0\.0\.1):\d+\//.test(item), `${path}: use a local path or an HTTPS URL.`);
        }
      } else if (Array.isArray(item)) {
        expect(item.length <= 500, `${path}: maximum 500 entries.`);
        item.forEach((child, i) => walk(child, `${path} ${i + 1}`));
      } else if (record(item)) Object.entries(item).forEach(([name, child]) => walk(child, `${path}.${name}`));
    };
    walk(value, key);
    if (key === 'site') {
      expect(text(value.brand?.name), 'Brand name is required.');
      expect(/^\d{10,15}$/.test(value.contact?.whatsappNumber || ''), 'WhatsApp number needs 10–15 digits including country code.');
      expect(integer(value.ordering?.maxQuantity, 1, 50), 'Maximum quantity must be a whole number from 1 to 50.');
      expect(value.ordering?.timeSlots?.length > 0, 'Add at least one order time slot.');
      expect(text(value.seo?.title), 'Page title is required.');
      expect(!value.legal?.fssaiNumber || /^\d{14}$/.test(value.legal.fssaiNumber), 'Enter a verified 14-digit FSSAI number or leave it empty.');
      const map = value.contact?.mapEmbedUrl;
      expect(!map || /^https:\/\/(www\.google\.com|maps\.google\.com)\//.test(map), 'Map embed must use Google Maps.');
    }
    if (key === 'products') {
      const products = value.products;
      expect(Array.isArray(products), 'Products must be a list.');
      const ids = new Set();
      (Array.isArray(products) ? products : []).forEach((product, index) => {
        const label = `Bake ${index + 1}`;
        expect(integer(product.id, 1, 999999999) && !ids.has(product.id), `${label}: product ID must be unique.`);
        ids.add(product.id);
        expect(text(product.name), `${label}: name is required.`);
        expect(['brownies', 'cakes', 'cookies', 'special', 'gifts'].includes(product.category), `${label}: choose a category.`);
        expect(['fresh', 'made-to-order', 'preorder', 'sold-out'].includes(product.availability), `${label}: choose availability.`);
        expect(product.priceOnRequest || money(product.price), `${label}: enter a price above zero or enable Price on request.`);
        expect(integer(product.leadTimeDays, 0, 30), `${label}: lead time must be 0–30 whole days.`);
        const variantIds = new Set();
        (product.variants || []).forEach((variant) => {
          expect(text(variant.name), `${label}: each option needs a name.`);
          expect(text(variant.id) && !variantIds.has(variant.id), `${label}: option IDs must be unique.`);
          variantIds.add(variant.id);
          expect(variant.priceOnRequest || money(variant.price), `${label}, ${variant.name || 'option'}: enter a price above zero or enable Quote.`);
        });
      });
    }
    const listKey = { offers: 'offers', gallery: 'images', testimonials: 'testimonials', faq: 'faqs' }[key];
    if (listKey) expect(Array.isArray(value[listKey]), `${key}: entries must be a list.`);
    if (key === 'faq') (value.faqs || []).forEach((item, i) => expect(text(item.q) && text(item.a), `FAQ ${i + 1}: add both a question and an answer.`));
    if (key === 'testimonials') (value.testimonials || []).forEach((item, i) => expect(text(item.name) && text(item.text) && integer(item.rating, 1, 5), `Review ${i + 1}: add a name, review and rating from 1–5.`));
    if (key === 'gallery') (value.images || []).forEach((item, i) => expect(text(item.url) && text(item.label), `Image ${i + 1}: add an image and caption.`));
    if (key === 'offers') {
      const ids = new Set();
      (value.offers || []).forEach((item, i) => {
        expect(text(item.id) && !ids.has(item.id), `Offer ${i + 1}: use a unique ID.`); ids.add(item.id);
        expect(text(item.title), `Offer ${i + 1}: add a title.`);
        expect(!item.startsAt || Number.isFinite(Date.parse(item.startsAt)), `Offer ${i + 1}: start date is invalid.`);
        expect(!item.endsAt || Number.isFinite(Date.parse(item.endsAt)), `Offer ${i + 1}: end date is invalid.`);
        expect(!item.startsAt || !item.endsAt || Date.parse(item.endsAt) >= Date.parse(item.startsAt), `Offer ${i + 1}: end date must follow the start date.`);
      });
    }
  }
  const productIds = new Set((next.products?.products || []).map((product) => product.id));
  if (next.homepage && next.products) {
    expect(!next.homepage.sections?.newDrop || productIds.has(next.homepage.newDrop?.productId), 'Choose an existing product for the homepage spotlight, or hide that section.');
    expect((next.homepage.bestSellers?.productIds || []).every((id) => productIds.has(id)), 'Best sellers must refer to existing products.');
  }
  return errors;
}

